import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import {
  Calendar, Clock, Search, Filter, AlertCircle,
  ChevronDown, FileText, Shield, CheckCircle,
  User, Book, Info
} from 'lucide-react';

// Helper Components
const StatusBadge = ({ status }) => {
  const styles = {
    'in-progress': 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    reopened: 'bg-orange-100 text-orange-800'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status] || 'bg-gray-100'}`}>
      {status?.replace('-', ' ').split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')}
    </span>
  );
};

const RiskLevelBadge = ({ level }) => {
  const styles = {
    none: 'bg-gray-100 text-gray-800',
    low: 'bg-blue-100 text-blue-800',
    moderate: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    severe: 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[level] || 'bg-gray-100'}`}>
      {level.charAt(0).toUpperCase() + level.slice(1)} Risk
    </span>
  );
};

const SectionTitle = ({ children, icon: Icon }) => (
  <div className="flex items-center space-x-2 mb-4 pb-2 border-b">
    {Icon && <Icon className="h-5 w-5 text-gray-500" />}
    <h3 className="text-lg font-medium text-gray-900">{children}</h3>
  </div>
);

const DataItem = ({ label, value, type = "text" }) => {
  if (!value && value !== 0) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderValue = () => {
    if (type === "date") return formatDate(value);
    if (type === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.length === 0 ? "None" : value.join(", ");
    return value;
  };

  return (
    <div className="py-2">
      <span className="text-sm font-medium text-gray-500">{label}:</span>
      <span className="ml-2 text-gray-900">{renderValue()}</span>
    </div>
  );
};
const SessionContent = ({ session, userRole, onContinueSession, onReopenSession,  selectedSessionRecord, 
  recordLoading  }) => {
  const canViewSensitiveData = ['counselor', 'hod'].includes(userRole);
  const canViewBasicData = ['student', 'staff','receptionist'].includes(userRole);
  const isInProgress = session.status === 'in-progress';
  const canContinueSession = ['counselor', 'hod'].includes(userRole) && isInProgress;
  const canReopenSession = ['hod'].includes(userRole);
  const isInitialSession = session.record?.sessionNumber === '1' || session.record?.sessionNumber === 1;


  const handleContinueSession = () => {
    if (session.appointment?._id) {
      onContinueSession(session.appointment._id);
    } else {
      console.error("Cannot continue session: Appointment ID is missing");
    }
  };

  const getDisplayName = (user) => {
    return user?.fullName || user?.username || 'N/A';
  };

  return (
    <div className="space-y-6">
      {/* Basic Session Information - Visible to all */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
        <DataItem
          label="Date"
          value={session.record?.sessionDate}
          type="date"
        />
        <DataItem
          label="Session Number"
          value={session.record?.sessionNumber}
        />
        {session.record?.followUpDate && (
          <DataItem
            label="Follow-up Date"
            value={session.record?.followUpDate}
            type="date"
          />
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">Status:</span>
          <StatusBadge status={session.status} />
        </div>
      </div>

      {/* Participants Section */}
      <div>
        <SectionTitle icon={User}>Participants</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Counselor</h4>
            <p className="font-medium">{getDisplayName(session.counselor)}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Client</h4>
            <p className="font-medium">{getDisplayName(session.client)}</p>
          </div>
        </div>
      </div>

      {/* Clinical Content - Only visible to authorized roles */}
      {canViewSensitiveData && (
        <>
          {/* Initial Session Content */}
          {isInitialSession ? (
            <>
              {/* Intake Information */}
              <div>
                <SectionTitle icon={Book}>Intake Assessment</SectionTitle>
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  {session.intake?.presentingProblems && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Presenting Problems</h5>
                      {Array.isArray(session.intake.presentingProblems) ? (
                        <div className="space-y-2">
                          {session.intake.presentingProblems.map((problem, index) => (
                            <p key={index} className="text-gray-600">{problem}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 whitespace-pre-wrap">{session.intake.presentingProblems}</p>
                      )}
                    </div>
                  )}

                  {session.intake?.diagnosis && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Initial Diagnosis</h5>
                      <p className="text-gray-600">{session.intake.diagnosis}</p>
                    </div>
                  )}

                  {session.intake?.goals && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Goals Discussed</h5>
                      {Array.isArray(session.intake.goals) ? (
                        <div className="space-y-2">
                          {session.intake.goals.map((goal, index) => (
                            <p key={index} className="text-gray-600">{goal}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 whitespace-pre-wrap">{session.intake.goals}</p>
                      )}
                    </div>
                  )}

                  {session.intake?.impactOnFunctioning && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Impact on Functioning</h5>
                      <div className="space-y-2">
                        {session.intake.impactOnFunctioning.current && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Current:</span>
                            <p className="text-gray-600 ml-2">{session.intake.impactOnFunctioning.current}</p>
                          </div>
                        )}
                        {session.intake.impactOnFunctioning.past && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Past:</span>
                            <p className="text-gray-600 ml-2">{session.intake.impactOnFunctioning.past}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {session.intake?.nextSteps && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Next Steps</h5>
                      <p className="text-gray-600 whitespace-pre-wrap">{session.intake.nextSteps}</p>
                    </div>
                  )}

                  {session.intake?.interventionsUsed && session.intake.interventionsUsed.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Interventions Used</h5>
                      <div className="flex flex-wrap gap-2">
                        {session.intake.interventionsUsed.map((intervention, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-uzima-maroon-50 text-uzima-maroon-600 rounded-full text-sm"
                          >
                            {intervention}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Mental Status Examination */}
              <div>
                <SectionTitle icon={Info}>Mental Status Examination</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  {Object.entries(session.mentalStatusExam || {}).map(([key, value]) => {
                    if (typeof value === 'object') {
                      return (
                        <div key={key} className="space-y-2">
                          <h5 className="font-medium text-gray-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </h5>
                          {Object.entries(value).map(([subKey, subValue]) => (
                            <div key={`${key}-${subKey}`} className="text-sm">
                              <span className="text-gray-500 capitalize">
                                {subKey.replace(/([A-Z])/g, ' $1').trim()}:
                              </span>
                              <span className="ml-2 text-gray-900">{subValue}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <DataItem
                        key={key}
                        label={key.replace(/([A-Z])/g, ' $1').trim()}
                        value={value}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Follow-up Session Content - Counseling Record */}
              {/* Progress Notes */}
              {/* Follow-up Session Content - Counseling Record */}
              <div>


                {recordLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
                  </div>
                ) : (
                  <div>
                    <SectionTitle icon={FileText}>On-Going Counseling Report</SectionTitle>

                    {recordLoading ? (
                      <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
                      </div>
                    ) : selectedSessionRecord ? (
                      <div className="space-y-6">
                        {/* Initial Diagnosis */}
                        {selectedSessionRecord.initialDiagnosis && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Initial Diagnosis</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.initialDiagnosis}</p>
                            </div>
                          </div>
                        )}

                        {/* Presenting Issues */}
                        {selectedSessionRecord.presentingIssues && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Presenting Issues</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.presentingIssues}</p>
                            </div>
                          </div>
                        )}

                        {/* Goals Discussed */}
                        {selectedSessionRecord.goalsDiscussed && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Goals Discussed</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.goalsDiscussed}</p>
                            </div>
                          </div>
                        )}

                        {/* Progress Notes */}
                        {selectedSessionRecord.progress && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Progress Notes</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.progress}</p>
                            </div>
                          </div>
                        )}

                        {/* Interventions */}
                        {selectedSessionRecord.interventionsUsed?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Interventions Used</h5>
                            <div className="flex flex-wrap gap-2">
                              {selectedSessionRecord.interventionsUsed.map((intervention, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-uzima-maroon-50 text-uzima-maroon-600 rounded-full text-sm"
                                >
                                  {intervention}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Prognosis */}
                        {selectedSessionRecord.prognosis && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Prognosis</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.prognosis}</p>
                            </div>
                          </div>
                        )}

                        {/* Plan for Next Session */}
                        {selectedSessionRecord.planForNextSession && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Plan for Next Session</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.planForNextSession}</p>
                            </div>
                          </div>
                        )}

                        {/* Homework */}
                        {selectedSessionRecord.homework && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Homework/Assignments</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700 whitespace-pre-wrap">{selectedSessionRecord.homework}</p>
                            </div>
                          </div>
                        )}

                        {/* Follow-up Date */}
                        {selectedSessionRecord.followUpDate && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Follow-up Date</h5>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-gray-700">
                                {new Date(selectedSessionRecord.followUpDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-500 text-center">No counseling record available for this session.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Safety Assessment - Common to both initial and follow-up */}
          <div>
            <SectionTitle icon={Shield}>Safety Assessment</SectionTitle>
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">Risk Level</span>
                <RiskLevelBadge level={session.safety?.riskLevel || 'none'} />
              </div>
              {session.safety?.safetyContractRequired && (
                <div className="flex items-center bg-yellow-50 p-3 rounded-lg text-yellow-700">
                  <Shield className="h-5 w-5 mr-2" />
                  <span>Safety Contract Required</span>
                </div>
              )}
              {session.safety?.concerns && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Safety Concerns</h5>
                  <p className="text-gray-600">{session.safety.concerns}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Plan and Homework - Visible to client after session */}
      {(canViewBasicData || canViewSensitiveData) && !isInProgress && (
        <div>
          <SectionTitle icon={CheckCircle}>Session Summary</SectionTitle>
          <div className="space-y-4">
            {session.record?.planForNextSession && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Plan for Next Session</h5>
                <p className="text-gray-600">{session.record.planForNextSession}</p>
              </div>
            )}
            {session.record?.homework && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Homework/Assignments</h5>
                <p className="text-gray-600">{session.record.homework}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end pt-4 space-x-4">
        {canContinueSession && (
          <button
            onClick={handleContinueSession}
            className="px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600 flex items-center"
          >
            <Clock className="h-5 w-5 mr-2" />
            Continue Session
          </button>
        )}

        {canReopenSession && session.status !== 'in-progress' && (
          <button
            onClick={() => onReopenSession(session._id)}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 flex items-center"
          >
            <AlertCircle className="h-5 w-5 mr-2" />
            Reopen Session
          </button>
        )}
      </div>
    </div>
  );
};

// Main SessionHistory Component
export const SessionHistory = () => {
  const navigate = useNavigate();
  const { get, put } = useApi();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [selectedSessionRecord, setSelectedSessionRecord] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  const fetchSessionRecord = async (sessionId) => {
    try {
      setRecordLoading(true);
      const response = await get(`/sessions/${sessionId}/record`);
      if (response.data?.status === 'success') {
        // Store the counseling record data
        setSelectedSessionRecord(response.data.data.record);
      }
    } catch (err) {
      setError(err.message || 'Failed to load counseling record');
    } finally {
      setRecordLoading(false);
    }
  };


  // Modify the function to handle expanding sessions
  const handleExpandSession = (sessionId) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      setSelectedSessionRecord(null); // Clear the record when collapsing
    } else {
      setExpandedSessionId(sessionId);
      fetchSessionRecord(sessionId);
    }
  };
  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await get('/sessions');
      if (response.data?.status === 'success') {
        setSessions(response.data.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };


  const handleContinueSession = (appointmentId) => {
    if (!appointmentId) {
      setError('Cannot continue session: Appointment ID is missing');
      return;
    }
    if (user.role === 'hod') {
      navigate(`/hod/sessions/${appointmentId}`);
    } else {
      navigate(`/counselor/sessions/${appointmentId}`);
    }
  };

  const handleReopenSession = async (sessionId, appointmentId) => {
    try {
      const response = await put(`/sessions/${sessionId}/reopen`);
      if (response.data?.status === 'success') {
        setSessions(prevSessions =>
          prevSessions.map(session =>
            session._id === sessionId ? { ...session, status: 'reopened' } : session
          )
        );
        navigate(`/hod/sessions/${appointmentId}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to reopen the session');
    }
  };

  const filteredSessions = sessions.filter(session => {
    const matchesStatus = filters.status === 'all' || session.status === filters.status;
    if (!filters.search) return matchesStatus;

    const searchLower = filters.search.toLowerCase();
    return matchesStatus && (
      session.client?.username?.toLowerCase().includes(searchLower) ||
      session.counselor?.username?.toLowerCase().includes(searchLower) ||
      session.client?.fullName?.toLowerCase().includes(searchLower) ||
      session.counselor?.fullName?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Session History</h1>
        <p className="mt-2 text-gray-600">
          View and manage your counseling sessions
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search sessions..."
                className="w-full pl-10 pr-4 py-2 border rounded-md"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  search: e.target.value
                }))}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                status: e.target.value
              }))}
              className="border rounded-md px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="reopened">Reopened</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Session List */}
      <div className="space-y-4">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              {sessions.length === 0
                ? 'No sessions found'
                : 'No matching sessions'
              }
            </h3>
            <p className="mt-1 text-gray-500">
              {sessions.length === 0
                ? 'No counseling sessions have been recorded yet.'
                : 'Try adjusting your search filters.'
              }
            </p>
          </div>
        ) : (
          filteredSessions.map(session => (
            <div
              key={session._id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                {/* Session Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Session {session.record?.sessionNumber} - {' '}
                      {user.role === 'student'
                        ? `with ${session.counselor?.fullName || session.counselor?.username}`
                        : `for ${session.client?.fullName || session.client?.username}`
                      }
                    </h3>
                    <div className="flex items-center mt-2 space-x-4">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(session.record?.sessionDate || session.createdAt).toLocaleDateString()}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {session.duration || '50 minutes'}
                      </span>
                      <StatusBadge status={session.status} />
                    </div>
                  </div>
                  {/* <button
                    onClick={() => handleExpandSession(session._id)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    aria-expanded={expandedSessionId === session._id}
                    aria-label="Toggle session details"
                  >
                    <ChevronDown
                      className={`h-5 w-5 transform transition-transform duration-200
      ${expandedSessionId === session._id ? 'rotate-180' : ''}`}
                    />
                  </button> */}
                  <button
                    onClick={() => setExpandedSessionId(
                      expandedSessionId === session._id ? null : session._id
                    )}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    aria-expanded={expandedSessionId === session._id}
                    aria-label="Toggle session details"
                  >
                    <ChevronDown
                      className={`h-5 w-5 transform transition-transform duration-200
                        ${expandedSessionId === session._id ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {/* Quick Summary - Always visible */}
                <div className="mt-4">
                  {session.presentingProblems?.length > 0 && user.role !== 'student' && (
                    <div className="flex flex-wrap gap-2">
                      {session.presentingProblems.slice(0, 3).map((problem, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {problem}
                        </span>
                      ))}
                      {session.presentingProblems.length > 3 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          +{session.presentingProblems.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Safety Alert - For counselors */}
                {['counselor', 'hod'].includes(user.role) &&
                  session.safety?.riskLevel !== 'none' && (
                    <div className="mt-4">
                      <div className="flex items-center space-x-2 text-yellow-600">
                        <Shield className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          Safety Risk Level:
                          <RiskLevelBadge level={session.safety.riskLevel} />
                        </span>
                      </div>
                    </div>
                  )}

                {/* Expanded Content */}
                {expandedSessionId === session._id && (
                  <div className="mt-6 pt-6 border-t">
                    <SessionContent
                      session={session}
                      userRole={user.role}
                      onContinueSession={handleContinueSession}
                      onReopenSession={handleReopenSession}
                      selectedSessionRecord={selectedSessionRecord} // Pass the data here
                      recordLoading={recordLoading} // Pass loading state
                    />
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg shadow-lg z-50">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistory;